
const TRACE_CACHE_TTL_MS = 60000;
const traceCache = new Map();

const normalizeBatchCode = (batchCode) => String(batchCode || '').trim();

/**
 * Fetches the full trace for a batch by its batch code.
 * Supports batch codes with slashes (e.g. BAT0012/Short/20260309).
 *
 * @param {string} batchCode - The batch code to trace
 * @param {Object} options - Fetch options
 * @param {AbortSignal} options.signal - Optional signal used to cancel stale requests
 * @returns {Promise<Object>} The trace response with batch, childBatches, timeline, status
 */
export const getTraceByBatchCode = async (batchCode, options = {}) => {
    const normalizedBatchCode = normalizeBatchCode(batchCode);
    if (!normalizedBatchCode) {
        throw new Error('Batch ID is required.');
    }

    const cached = traceCache.get(normalizedBatchCode);
    if (cached && Date.now() - cached.timestamp < TRACE_CACHE_TTL_MS) {
        return cached.data;
    }

    const url = `${process.env.REACT_APP_HOST}open/trace?batchId=${encodeURIComponent(normalizedBatchCode)}`;

    const response = await fetch(url, { signal: options.signal });
    
    if (!response.ok) {
        throw new Error(`Trace API returned ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    const data = result.content || result.data;
    traceCache.set(normalizedBatchCode, {
        timestamp: Date.now(),
        data
    });
    return data;
};

/**
 * Helper to extract a property value from a list of properties.
 * Properties can have either `name` or `key` as the label field.
 * 
 * @param {Array} properties - Array of property objects
 * @param {string} key - The property key/name to find
 * @returns {string|null} The property value or null
 */
export const getPropertyValue = (properties, key) => {
    if (!properties || !Array.isArray(properties)) return null;
    const normalize = (value) => String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    const normalizedKey = normalize(key);
    const prop = properties.find(p => 
        normalize(p.name || p.key) === normalizedKey
    );
    if (!prop || prop.value === null || prop.value === undefined) return null;
    const value = String(prop.value).trim();
    return value ? value : null;
};

export const formatLocation = (location) => {
    if (!location) return '—';
    const parts = String(location).split(',').map(value => Number(value.trim()));
    if (parts.length !== 2 || parts.some(value => !Number.isFinite(value))) return '—';
    const [lat, lng] = parts;
    if ((lat === 0 && lng === 0) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return '—';
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

/**
 * Formats a LocalDateTime string (yyyy-MM-dd'T'HH:mm:ss) to "DD Mon • HH:MM AM/PM"
 * 
 * @param {string} dateTimeStr - ISO datetime string
 * @returns {string} Formatted date time
 */
export const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '—';
    
    try {
        const date = new Date(dateTimeStr);
        if (isNaN(date.getTime())) return '—';
        
        const day = date.getDate().toString().padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        
        let hours = date.getHours();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        if (hours === 0) hours = 12;
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day} ${month} • ${hours}:${minutes} ${ampm}`;
    } catch (e) {
        return '—';
    }
};

/**
 * Maps process type to timeline step icon name
 */
export const PROCESS_TYPE_ICONS = {
    'GRN_BATCH_CREATE': 'package',
    'GRN_BATCH_CLOSE': 'truck',
    'FACTORY_RECEIVED': 'clipboard-check',
    'FACTORY_PROCESS': 'settings',
    'FACTORY_OUT': 'clipboard-check',
};
